import { EventEmitter, Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { MODEL_API, ORGANISATION_API, RepositoryService } from "../linq-utils/repository.service";
import { Organisation, ModelDetails, TagGroup, Project, createOrganisation, InformationAsset, InsightsFilter, ModelSummary, EmissionFactor } from "../common/organisation";
import { Observable, firstValueFrom } from "rxjs";
import { DEFAULT_URL } from "../app-urls";
import { UserService } from "../user/user.service";
import { v4 } from "uuid";
import { MatDialog } from "@angular/material/dialog";
import { OrganisationDialogComponent } from "./organisation-dialog.component";
import { USER_ROLE, User } from "../common/user";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../ui-components/dialogs/confirm-dialog.component";
import { ERROR_CODE, getUserMessage, LinqException } from "../common/exceptions";

const COMPONENT = "Organisation service"

@Injectable({
    providedIn: 'root'
  })
export class OrganisationService {
    organisation:Organisation
    users:User[]

    updatedOrganisationEvent = new EventEmitter<string>()

    constructor(private repoService: RepositoryService, private userService: UserService,
        private router:Router, private snackBar:MatSnackBar, private dialog: MatDialog) {
    }

    async checkOrganisationDataForRoute(organisationKey:string):Promise<Organisation> {
        const user = this.userService.getUser()
        if (!user) {
            this.snackBar.open("No user information found. Redirecting to login", "close");
            this.router.navigateByUrl("auth")
            return null
        }

        if (organisationKey && organisationKey.length > 0) {
            if (!this.organisation || this.organisation._key !== organisationKey) {
                await this.loadOrganisation(organisationKey)
                // loaded alongside; a failure has been shown, and the page works without them
                if (this.organisation) this.getUsersForOrganisation(this.organisation).then(users => this.users = users, () => {})
            }
        }else this.organisation = null

        return this.organisation
    }

    clear() {
        this.organisation = null
    }

    async reloadOrganisation() {
        if (this.organisation) this.updatedOrganisationEvent.emit(this.organisation._key)
    }

    private async loadOrganisation(organisationId:string) {
        const SERVICE = "Load organisation"
        this.organisation = await this.repoService.callSingleAPI(this.repoService.getData<Organisation>(ORGANISATION_API(organisationId)), COMPONENT, SERVICE, this.userService.getUser(), this.organisation)
    }

    async getOrganisation(organisationId:string) {
        const SERVICE = "Get organisation"
        return await this.repoService.callSingleAPI(this.repoService.getData<Organisation>(ORGANISATION_API(organisationId)), COMPONENT, SERVICE, this.userService.getUser(), this.organisation)
    }

    async getUsersForOrganisation(organisation:Organisation):Promise<User[]> {
        const SERVICE = "Get users for organisation"

        return await this.repoService.callSingleAPI(this.repoService.getData<User[]>(ORGANISATION_API(organisation._key) + '/users'), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    getModel(modelKey:string) {
        return this.organisation && this.organisation.models && this.organisation.models.find(v => v._key === modelKey)
    }

    async getOrganisations() {
        const SERVICE = "Get organisations"
        return await this.repoService.callSingleAPI(this.repoService.getData<Organisation>('api/organisations'), COMPONENT, SERVICE, this.userService.getUser())
    }

    // makes a change, then says what was done (unless msg is false, for one of several changes); an error has been
    // shown and is passed on
    private async callOrganisationAPI(apiCall:Observable<any>, url?:string, msg?:string|false) {
        const SERVICE = "Call organisation API"
        await this.repoService.callSingleAPI(apiCall, COMPONENT, SERVICE, this.userService.getUser(), this.organisation)
        if (msg !== false) this.snackBar.open(msg || "Organisation details updated", "close", {duration:5000});

        if (url) {
            this.organisation = undefined
            this.router.navigateByUrl(url)
        }
    }

    private async createOrganisation(organisationToCreate:Organisation, navigateTo:boolean=true) {
        organisationToCreate._key = v4()
        await this.callOrganisationAPI(this.repoService.create(ORGANISATION_API(organisationToCreate._key), organisationToCreate), navigateTo && DEFAULT_URL(organisationToCreate._key), "Organisation created")
    }

    public async closeOrganisation(organisation:Organisation) {
        const messages = [
            `Close the organisation ${organisation.name}? It stays available until the end of the month, then closes.`,
            "After two months all of its data may be removed. To reinstate it before then, contact LINQ support.",
        ]
        if (!organisation.isExemptFromPayment) messages.push("A final invoice will be sent based on usage at the end of the month.")

        const dialogData = new ConfirmDialogModel("Close organisation account", messages, "close", false, "Close organisation", "Cancel");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(organisation._key) + "/close", {}), undefined, "The organisation will close at the end of the month")
                this.updatedOrganisationEvent.emit(organisation._key)
            }
        });

    }

    private async updateOrganisation(organisationToSave?:Organisation, url?:string) {
        organisationToSave = organisationToSave || this.organisation

        await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(organisationToSave._key), organisationToSave), url)
    }

    public async saveOrganisation(organisation:Organisation, isNew?:boolean, url?:string) {
        if (!isNew) await this.updateOrganisation(organisation, url)
        else await this.createOrganisation(organisation)

        this.updatedOrganisationEvent.emit(organisation._key)
    }

    // saves a new list of emission factors, putting the old one back if the save fails; the error has already been shown
    public async saveEmissionFactorList(organisation:Organisation, factors:EmissionFactor[]) {
        const previous = organisation.emissionFactors
        organisation.emissionFactors = factors
        try {
            await this.saveEmissionFactors(organisation)
            return true
        }catch(err) {
            organisation.emissionFactors = previous
            return false
        }
    }

    public async saveEmissionFactors(organisation:Organisation) {
        await this.repoService.callSingleAPI(this.repoService.update(ORGANISATION_API(organisation._key) + "/emissionfactors", organisation.emissionFactors), COMPONENT, "Update emission factors", this.userService.getUser(), organisation)

        this.updatedOrganisationEvent.emit(organisation._key)
    }


    async deleteOrganisation(organisation:Organisation, url?:string) {
        const SERVICE = "Delete organisation"
        await this.repoService.callSingleAPI(this.repoService.delete(ORGANISATION_API(organisation._key)), COMPONENT, SERVICE, this.userService.getUser(), organisation)
        this.snackBar.open("Organisation deleted", "close", {duration:5000});
        if (url) this.router.navigateByUrl(url)
    }

    async saveModel(model:ModelDetails, projectKey:string, isNew:boolean, file?:File) {
        if (isNew && !model._key) model._key = v4()

        if (file) {
            await this.createModelWithGraphFile(file, model, projectKey, isNew)
        }else if (isNew) {
            await this.createModel(model)
        }else {
            await this.updateModel(model)
        }
        const existing = this.organisation.models.find(m => m._key === model._key)
        if (!existing) {
            if (isNew) this.organisation.models.push(model)
        }else if (existing !== model) Object.assign(existing, model)
        this.updatedOrganisationEvent.emit(this.organisation._key)
    }

    private async createModelWithGraphFile(file:File, model:ModelDetails, projectKey:string, isNew?:boolean) {
        const formData = new FormData();
        formData.append("jsonfile", file);
        formData.append("workspace", JSON.stringify(model))

        if (isNew) {
            await this.callOrganisationAPI(this.repoService.putFile(MODEL_API(this.organisation._key, model._key, projectKey) + '/upload', formData), undefined, "Model created from the file")
            return
        }
        const url = MODEL_API(this.organisation._key, model._key, projectKey) + '/upload'
        try {
            await this.repoService.callSingleAPI(this.repoService.postFile(url, formData), COMPONENT, "Upload model", this.userService.getUser(), this.organisation, undefined, undefined,
                // projects having changed the model is explained below, with the choice to reset them
                e => e?.msgCode !== ERROR_CODE.ERR236)
        }catch(err:any) {
            if (err?.msgCode !== ERROR_CODE.ERR236 || !await this.confirmResetProjects(err)) throw err
            formData.append("resetProjects", "true")
            await this.callOrganisationAPI(this.repoService.postFile(url, formData), undefined, "Model reset in its projects and updated from the file")
            return
        }
        this.snackBar.open("Model updated from the file", "close", {duration:5000})
    }

    // an upload replaces a current state model's nodes, so the projects that changed the model would be left joined to
    // nodes that are gone. An organisation administrator can reset the model to current state in them and upload
    private async confirmResetProjects(err:LinqException):Promise<boolean> {
        const projects:string[] = err.info?.projects || []
        const one = projects.length === 1
        const isAdmin = this.organisation?.role === USER_ROLE.ADMIN
        const messages = [getUserMessage(err)]
        if (isAdmin) messages.push(`Resetting discards the changes ${one ? 'that project has' : 'those projects have'} made to this model, so ${one ? 'it shows' : 'they show'} the uploaded model as it is. Other models in ${one ? 'it' : 'them'} aren't affected.`)
        else messages.push("Only an organisation administrator can reset the model in projects.")
        const data = new ConfirmDialogModel("Changed in projects", messages, undefined, false, isAdmin ? `Reset ${one ? 'project' : `${projects.length} projects`} and upload` : "OK", "Cancel")
        if (!isAdmin) data.noText = undefined
        const result = await firstValueFrom(this.dialog.open(ConfirmDialogComponent, {maxWidth: "480px", data}).afterClosed())
        return isAdmin && !!result?.button?.startsWith("Reset")
    }

    private async createModel(model:ModelDetails) {
        await this.callOrganisationAPI(this.repoService.create(this.getModelApi(model._key), model), undefined, "Model created")
    }

    async requestExtension(organisation:Organisation, notes:string) {
        await this.callOrganisationAPI(this.repoService.create(ORGANISATION_API(organisation._key) + '/extension', {notes}), undefined, "Request for extension submitted.")
    }

    // one at a time; each is shown as restored as soon as it is, so a failure part way leaves the list right
    async restoreModels(modelKeys:string[]) {
        for (const key of modelKeys) {
            await this.callOrganisationAPI(this.repoService.update(this.getModelApi(key) + `/restore`, {}), undefined, false)
            const model = this.getModel(key)
            if (model) model.deletedDate = undefined
        }
        this.snackBar.open(modelKeys.length === 1 ? "Model restored" : `${modelKeys.length} models restored`, "close", {duration:5000})
    }

    private async updateModel(model:ModelDetails) {
        await this.callOrganisationAPI(this.repoService.update(this.getModelApi(model._key), model), undefined, "Model details saved")
    }

    async deleteModel(model:ModelDetails, projectKey:string, permanently:boolean) {
        if (permanently) {
            await this.callOrganisationAPI(this.repoService.delete(this.getModelApi(model._key, projectKey) + "?permanently=true"), undefined, "Model deleted")
            this.organisation.models = this.organisation.models.filter(m => m._key !== model._key)
        }else {
            await this.callOrganisationAPI(this.repoService.delete(this.getModelApi(model._key, projectKey)), undefined, "Model archived")
            model.deletedDate = new Date()
            // the organisation's own copy, when given another (as a model table row does)
            const listed = this.getModel(model._key)
            if (listed && listed !== model) listed.deletedDate = model.deletedDate
        }

    }

    async deleteModelKeys(modelKeys:string[]) {
        for (const k of modelKeys) {
            await this.callOrganisationAPI(this.repoService.delete(this.getModelApi(k) + "?permanently=true"), undefined, false)
            this.organisation.models = this.organisation.models.filter(m => m._key !== k)
        }
        this.snackBar.open(modelKeys.length === 1 ? "Model deleted" : `${modelKeys.length} models deleted`, "close", {duration:5000})
    }

    async deleteTagGroup(tagGroup:TagGroup) {
        await this.callOrganisationAPI(this.repoService.delete(ORGANISATION_API(this.organisation._key) + `/tags/${tagGroup._key}`), undefined, "Tag group deleted")
        this.organisation.tagGroups = (this.organisation.tagGroups || []).filter(tg => tg._key !== tagGroup._key)

        this.updatedOrganisationEvent.emit(this.organisation._key)
    }

    async updateTagGroup(tagGroup:TagGroup) {
        await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(this.organisation._key) + `/tags/${tagGroup._key}`, tagGroup), undefined, "Tag group saved")
    }

    async createTagGroup(tagGroup:TagGroup) {
        await this.callOrganisationAPI(this.repoService.create(ORGANISATION_API(this.organisation._key) + `/tags/${tagGroup._key}`, tagGroup), undefined, "Tag group created")
        this.organisation.tagGroups = [...(this.organisation.tagGroups || []), tagGroup]
        this.updatedOrganisationEvent.emit(this.organisation._key)
    }

    async deleteInformationAsset(assetKey:string) {
        await this.callOrganisationAPI(this.repoService.delete(ORGANISATION_API(this.organisation._key) + `/informationassets/${assetKey}`), undefined, "Information asset deleted")
        this.organisation.informationAssets = (this.organisation.informationAssets || []).filter(a => a._key !== assetKey)
        this.updatedOrganisationEvent.emit(this.organisation._key)
    }

    // the local list only changes once the server has saved; returns the saved asset, or undefined if it failed
    // (the error has already been shown)
    async updateInformationAsset(asset:InformationAsset):Promise<InformationAsset|undefined> {
        try {
            await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(this.organisation._key) + `/informationassets/${asset._key}`, asset), undefined, 'Information asset saved')
        }catch(err) {
            return undefined
        }
        const existing = this.organisation.informationAssets?.find(a => a._key === asset._key)
        if (existing) Object.assign(existing, asset)
        return existing || asset
    }

    async createInformationAsset(asset:InformationAsset):Promise<InformationAsset|undefined> {
        try {
            await this.callOrganisationAPI(this.repoService.create(ORGANISATION_API(this.organisation._key) + `/informationassets/${asset._key}`, asset), undefined, 'Information asset created')
        }catch(err) {
            return undefined
        }
        if (!this.organisation.informationAssets) this.organisation.informationAssets = []
        this.organisation.informationAssets.push(asset)
        this.updatedOrganisationEvent.emit(this.organisation._key)
        return asset
    }

    async deleteProject(project:Project) {
        await this.callOrganisationAPI(this.repoService.delete(ORGANISATION_API(this.organisation._key, project._key)), undefined, 'Project deleted')
        this.organisation.projects = this.organisation.projects.filter(p => p._key !== project._key)
    }

    async updateProject(project:Project) {
        await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(this.organisation._key, project._key), project), undefined, 'Project updated')
        // update in place so pages holding this project see the change, and give the list a new identity
        const existing = this.organisation.projects.find(p => p._key === project._key)
        if (existing) Object.assign(existing, project)
        this.organisation.projects = [...this.organisation.projects]
        this.updatedOrganisationEvent.emit(this.organisation._key)
    }

    async createProject(project:Project) {
        await this.callOrganisationAPI(this.repoService.create(ORGANISATION_API(this.organisation._key, project._key), project), undefined, 'Project created')
        // the creator can manage the project; with organisation access their organisation role applies
        project.role = project.useOrganisationAccess ? this.organisation.role : USER_ROLE.ADMIN
        this.organisation.projects = [...this.organisation.projects, project]
    }

    // makes a new project as a copy of a project (its changes, the models made in it, and its people and
    // infrastructure), so a future state can build on it. The server works out its insights before it returns
    async copyProject(from:Project, project:Project) {
        project.sandbox = !!from.sandbox
        const made = await this.repoService.callSingleAPI(this.repoService.create(`${ORGANISATION_API(this.organisation._key, from._key)}/copy`, project), COMPONENT, "Copy project", this.userService.getUser(), this.organisation) as Project|undefined
        // the server keeps the key given, unless a project has it already
        if (made?._key) project._key = made._key
        this.snackBar.open('Project created', 'close', {duration:5000})
        project.role = project.useOrganisationAccess ? this.organisation.role : USER_ROLE.ADMIN
        this.organisation.projects = [...this.organisation.projects, project]
    }

    // sends a request to the administrators of the project or model; returns whether it was sent (a failure has already been shown)
    async requestAccess(role:USER_ROLE, notes:string, project?:Project, model?:ModelDetails) {
        const SERVICE = "Request access"
        const request = {role, notes}
        const url = model ? this.getModelApi(model._key) + "/access" : ORGANISATION_API(this.organisation._key, project._key) + "/access"

        try {
            await this.repoService.callSingleAPI(this.repoService.create(url, request), COMPONENT, SERVICE, this.userService.getUser(), this.organisation)
        }catch(err) {
            return false
        }
        this.snackBar.open(`Request sent to the administrators of ${model ? model.name : project.name}`, "close", {duration:5000})
        return true
    }

    async updateOrganisationAccess(user:User, role:USER_ROLE) {
        await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(this.organisation._key) + `/users/${user._key}`, {role}), undefined, "User role updated")
        const access = this.organisation.users?.find(u => u.userKey === user._key)
        if (access) access.access = role
        // changing your own role changes what you can do here
        if (user._key === this.userService.getUser()?._key) this.organisation.role = role
        this.users = this.users.map(u => u._key === user._key ? {...u, role} : u)
    }

    async removeOrganisationAccess(user:User) {
        // removing yourself takes away your access, so leave the organisation
        const leaving = user._key === this.userService.getUser()?._key
        await this.callOrganisationAPI(this.repoService.delete(ORGANISATION_API(this.organisation._key) + `/users/${user._key}`), leaving ? DEFAULT_URL() : undefined, "User removed from organisation")
        if (leaving) return
        this.organisation.users = this.organisation.users.filter(u => u.userKey !== user._key)
        this.users = this.users.filter(u => u._key !== user._key)
    }

    async acceptInvite(organisation:any) {
        await this.callOrganisationAPI(this.repoService.update(ORGANISATION_API(organisation._key) + '/accept', {}), undefined, "Invitation accepted")
        organisation.activated = true
    }

    getModelApi(key:string, projectKey?:string) {
        return ORGANISATION_API(this.organisation._key, projectKey) + `/model-details/${key}`
    }

    // getting started saves the organisation its first model goes in, then the model. Separate, so a model that fails
    // can be tried again without the organisation. The steps of getting started show their own progress, so nothing is said
    async saveGettingStartedOrganisation(organisation:Organisation, isNew:boolean) {
        const api = ORGANISATION_API(organisation._key)
        await this.callOrganisationAPI(isNew ? this.repoService.create(api, organisation) : this.repoService.update(api, organisation), undefined, false)
    }

    async addModelDetails(organisationKey:string, model:ModelDetails) {
        await this.callOrganisationAPI(this.repoService.create(ORGANISATION_API(organisationKey) + `/model-details/${model._key}`, model), undefined, false)
    }

    openOrganisationDialog(organisation?:Organisation) {
        const isNew = !organisation
        organisation = organisation || createOrganisation()

        return this.dialog.open(OrganisationDialogComponent, {
            data: {organisation, isNew},
            width: "600px",
            hasBackdrop:true
        });
    }

    // saves the filter and keeps a copy in the organisation's list; returns the saved filter, or undefined if it failed
    // (the error has already been shown)
    public async saveInsightsFilter(filter:InsightsFilter):Promise<InsightsFilter|undefined> {
        const SERVICE = "Save insights filter"
        const user = this.userService.getUser()
        // sent explicitly so a cleared node type is saved as cleared
        const data = {...filter, nodeType: filter.nodeType ?? null} as InsightsFilter

        try {
            if (filter._key) {
                await this.repoService.callSingleAPI(this.repoService.update(ORGANISATION_API(this.organisation._key) + '/filters/' + filter._key, data), COMPONENT, SERVICE, user)
            } else {
                data._key = v4()
                data.owner = user._key
                await this.repoService.callSingleAPI(this.repoService.create(ORGANISATION_API(this.organisation._key) + '/filters', data), COMPONENT, SERVICE, user)
            }
        }catch(err) {
            return undefined
        }

        const saved = structuredClone({...data, nodeType: filter.nodeType}) as InsightsFilter
        this.organisation.insightsFilters = [...this.organisation.insightsFilters.filter(f => f._key !== saved._key), saved]
        this.snackBar.open("Insights filter saved", "close", {duration:5000});
        return saved
    }

    public async deleteInsightsFilter(filter:InsightsFilter):Promise<boolean> {
        const SERVICE = "Delete insights filter"

        try {
            await this.repoService.callSingleAPI(this.repoService.delete(ORGANISATION_API(this.organisation._key) + '/filters/' + filter._key), COMPONENT, SERVICE, this.userService.getUser())
        }catch(err) {
            return false
        }
        this.organisation.insightsFilters = this.organisation.insightsFilters.filter(f => f._key !== filter._key)
        this.snackBar.open("Insights filter deleted", "close", {duration:5000});
        return true
    }

    public async getAdministrators() {
        const user = this.userService.getUser()
        if (user?.role !== USER_ROLE.ADMIN) return

        try {
            return await this.repoService.callSingleAPI(this.repoService.getData('api/admin/organisations/users/administrators'), COMPONENT, "Get administrators", user)
        }catch(err) {
            this.snackBar.open("An error happened while loading administrators. Please try again.", "close", {duration:5000});
        }

    }

}